import Dropdown from "app/components/Dropdown";
import { CheckboxActiveIcon, CloseIcon } from "app/components/Icons";
import CheckboxIcon from "app/components/Icons/CheckboxIcon";
import Pill from "app/components/Pill";
import { ILabelValue } from "app/containers/Features/containers/TeamSettings/container/Projects/container/CustomFields/types";
import clsx from "clsx";
import React, { useMemo, useState } from "react";

interface Props {
  onChange: (val: string[]) => void;
  value: string | string[];
  error?: string;
  options: ILabelValue[];
}

function toSelectionArray(value: string | string[]): string[] {
  if (Array.isArray(value)) {
    return value.filter((v) => v !== "" && v != null);
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
}

const LabelSelection: React.FC<Props> = ({
  onChange,
  value,
  error,
  options,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selected = useMemo(() => toSelectionArray(value), [value]);

  const isOptionSelected = (opt: ILabelValue) =>
    selected.some(
      (s) =>
        s === opt.labelName || (!!opt._id && String(opt._id) === String(s)),
    );

  const handleToggleOption = (option: ILabelValue) => {
    const name = option.labelName;
    const id = option._id != null ? String(option._id) : undefined;
    const base = selected;
    if (isOptionSelected(option)) {
      onChange(base.filter((s) => s !== name && (!id || s !== id)));
    } else {
      onChange([...base.filter((s) => !id || s !== id), name]);
    }
  };

  const MenuItems = options?.map((ele) => {
    if (!ele) return { label: "", action: () => {} };
    const checked = isOptionSelected(ele);
    return {
      label: (
        <button
          type="button"
          className="flex gap-2 w-full items-center justify-between"
          onClick={(e) => {
            e.preventDefault();
            handleToggleOption(ele);
          }}
        >
          {checked ? (
            <CheckboxActiveIcon size={20} />
          ) : (
            <CheckboxIcon size={18} />
          )}
          <Pill
            backgroundColor={ele?.labelColor}
            name={ele?.labelName}
            className="flex-1 !px-4 !py-3 text-sm font-normal text-white max-w-[80%] truncate "
          />
        </button>
      ),
      action: () => {},
    };
  });

  const getSelectedLabels = () => (
    <div className="flex items-center gap-2 flex-wrap">
      {selected.map((entry) => {
        const label = options?.find(
          (opt) => opt.labelName === entry || String(opt._id) === String(entry),
        );
        const key = String(label?._id ?? label?.labelName ?? entry);
        return (
          <div
            key={key}
            className="flex items-center gap-2 px-3 py-1 rounded group"
            style={{ backgroundColor: label?.labelColor || "#ccc" }}
          >
            <span className="text-sm text-white font-normal truncate max-w-[100px]">
              {label?.labelName ?? entry}
            </span>
            <button
              type="button"
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onChange(
                  selected.filter(
                    (s) =>
                      s !== entry &&
                      (!label?._id || String(s) !== String(label._id)),
                  ),
                );
                setIsOpen(false);
              }}
            >
              <CloseIcon color="#FFFFFF" />
            </button>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="w-full relative h-full ">
      <Dropdown
        trigger={
          <button
            type="button"
            className={clsx(
              "h-[42px] w-full min-h-max data-[state=open]:border-secondary data-[state=open]:outline-secondary data-[state=open]:border text-start px-3 text-sm text-black",
              error && "border-danger",
            )}
          >
            {selected.length > 0 ? getSelectedLabels() : "-"}
          </button>
        }
        projectInfoIcon={
          <div className="flex items-center justify-between px-4 h-[50px]  border-b border-gray-100 ">
            <span className="text-secondary text-sm font-semibold leading-[21px] uppercase">
              select an option
            </span>
          </div>
        }
        menuItems={MenuItems}
        open={isOpen}
        setOpen={setIsOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      />
    </div>
  );
};

export default LabelSelection;
